import { CookieEnum, eraseCookie, getCookie } from "@/shared/utils/cookie";
import { userVar } from "./reactive-variable";
import { ensureCustomerUserPrefix } from "./loginHelpers";
import { decodeToken, isUsableCustomerToken } from "./customer-token";

export { decodeToken, isUsableCustomerToken };

/**
 * Retrieves the CLIENT_CENTRAL_TOKEN from cookies first, then localStorage
 * @returns The token string if found, null otherwise
 */
export const getClientCentralToken = (): string | null => {
  const cookieToken = getCookie(CookieEnum.CLIENT_CENTRAL_TOKEN);
  if (cookieToken) {
    return cookieToken;
  }

  if (typeof window !== "undefined") {
    const localStorageToken = localStorage.getItem(
      CookieEnum.CLIENT_CENTRAL_TOKEN
    );
    if (localStorageToken) {
      return localStorageToken;
    }
  }

  return null;
};

/**
 * Checks if user has a valid token before making checkSession request
 * @returns boolean indicating if token exists
 */
export const hasValidToken = (): boolean => {
  return getClientCentralToken() !== null;
};

/**
 * Clears customer auth cookies, localStorage token, and in-memory user.
 * Browser-only for cookie/localStorage writes.
 */
export function clearCustomerAuthState(): void {
  eraseCookie(CookieEnum.AUTH_TOKEN, { sameSite: "strict" });
  eraseCookie(CookieEnum.CLIENT_CENTRAL_TOKEN, { sameSite: "strict" });
  if (typeof window !== "undefined") {
    localStorage.removeItem(CookieEnum.CLIENT_CENTRAL_TOKEN);
  }
  userVar(undefined);
}

/**
 * Reads auth-token and returns it only if it is still usable.
 * Expired or malformed tokens are cleared and not returned.
 */
export function getUsableAuthToken(): string | null {
  const token = getCookie(CookieEnum.AUTH_TOKEN);
  if (!token) return null;
  if (!isUsableCustomerToken(token)) {
    clearCustomerAuthState();
    return null;
  }
  return token;
}

export function getBearerTokenFromHeaders(
  headers?: Record<string, string> | null
): string | null {
  if (!headers) return null;
  const raw = headers.authorization || headers.Authorization;
  if (!raw) return null;
  const token = raw.replace(/^Bearer\s+/i, "").trim();
  return token || null;
}

/**
 * Token for an outgoing GraphQL request.
 * Prefer a usable Bearer token already on the operation (login/signup `Me`
 * runs before the auth cookie is written). Otherwise use the cookie.
 * Incoming tokens are checked first so an expired cookie is not cleared
 * during a fresh login.
 */
export function resolveOutgoingAuthToken(
  headers?: Record<string, string> | null
): string | null {
  const incoming = getBearerTokenFromHeaders(headers);
  if (incoming && isUsableCustomerToken(incoming)) {
    return incoming;
  }
  return getUsableAuthToken();
}

/**
 * Validates existing token and updates user context without backend call
 * @returns boolean indicating if token was valid and user updated
 */
export const validateAndUpdateUserFromToken = (): boolean => {
  const token = getClientCentralToken();
  if (!token) {
    return false;
  }

  try {
    const decodedToken = decodeToken(token);
    if (!decodedToken || !isUsableCustomerToken(token)) {
      clearCustomerAuthState();
      return false;
    }

    const userData = {
      id: ensureCustomerUserPrefix(decodedToken._id || decodedToken.id),
      name: decodedToken.name,
      email: decodedToken.email,
      phone: decodedToken.phone,
      isEmailVerfied: decodedToken.emailVerified ?? false,
      isPhoneVerified: decodedToken.phoneVerified ?? false,
      token: token,
    };

    userVar(userData);
    return true;
  } catch (error) {
    clearCustomerAuthState();
    return false;
  }
};

/**
 * Determines if checkSession request should be made
 * Only makes request if no token is found in cookies or localStorage
 * @returns boolean indicating if checkSession should be called
 */
export const shouldCheckSession = (): boolean => {
  return !hasValidToken();
};
